import { GeometryType } from "@iventis/domain-model/model/geometryType";
import { sidebarPadding, styled, formGap, fontSizes } from "@iventis/styles";
import { Theme } from "@emotion/react";
import GeoJSON from "geojson";
import { Content } from "@iventis/translations/content/typed-content";
import { MapModuleLayer } from "@iventis/map-engine";
import React, { FunctionComponent } from "react";
import { useFunctionality } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { useUpgradePlanAlert } from "@iventis/permissions/src/upgrade-subscription-alert";
import { AnalysisType } from "@iventis/domain-model/model/analysisType";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { analysisToolMonitoringMetric } from "@iventis/observability-and-monitoring";
import ExpandableButton, { ExpandableAdornmentType } from "@iventis/components/src/expandable-button";
import { useSelector } from "react-redux";
import { RootState } from "@iventis/plans/src/state/root.store";
import { LocalGeoJsonObject, MapObjectProperties } from "@iventis/map-types";
import { PopoverContainer } from "@iventis/components/src/popover-container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Subtitle } from "@iventis/styles/src/components/texts";
import { InteractiveElement } from "@iventis/styles/src/components";
import { CSSTransition } from "react-transition-group";
import { AnalysisDistanceComponent } from "./analysis-distance";
import { AnalysisWalkTimeComponent } from "./analysis-walk-time";
import { AnalysisFlowRateComponent } from "./analysis-flow-rate";
import { AnalysisAreaComponent } from "./analysis-area";
import { AnalysisCapacityComponent } from "./analysis-capacity";
import { AnalysisCountComponent } from "./analysis-count";
import { UnitSelector } from "./unit-selector";

export const AnalysisTools: FunctionComponent<{
    mapId: string;
    layers: MapModuleLayer[];
    selectedObjects: LocalGeoJsonObject["feature"][];
    unitOfMeasurement: UnitOfMeasurement;
    userId: string;
}> = ({ mapId, selectedObjects, unitOfMeasurement, layers }) => {
    const selectedAnalysisTool = useSelector((state: RootState) => state.mapReducer.selectedAnalysisTool);
    const translate = useIventisTranslate();
    const feature = selectedObjects[0];
    const geometryType = feature?.geometry?.type;

    const upgradePlan = useUpgradePlanAlert();

    const functionality = useFunctionality();

    const analysisTypesEnabled = functionality.analysisTypes;

    const typeEnabled = (analysisType: AnalysisType) => analysisTypesEnabled.includes(analysisType);

    const getAdornment = (analysisType: AnalysisType) => (typeEnabled(analysisType) ? ExpandableAdornmentType.Chevron : ExpandableAdornmentType.Upgrade);

    const handleAnalysisToolClicked = (expanded: boolean, analysisType: AnalysisType) => {
        // Only analysis tools which are restricted by payment plans is FlowRate and Capacity
        const doesAnalysisTypeNeedPlan = analysisType === AnalysisType.FlowRate || analysisType === AnalysisType.Capacity;
        if (doesAnalysisTypeNeedPlan && !typeEnabled(analysisType)) {
            upgradePlan.open(analysisType);
        } else if (expanded) {
            analysisToolMonitoringMetric(analysisType);
        }
    };

    // used to keep track of transitioning child
    const nodeRef = React.useRef(null);

    const [isAnalysisToolsMinimised, setAnalysisToolMinimised] = React.useState(false);

    return (
        <PopoverContainer className="popup" testId="analysis-tool-panel">
            <div className="header">
                <div className="title">
                    <FontAwesomeIcon icon={{ prefix: "far", iconName: "ruler" }} />
                    <Subtitle>
                        <StyledTitle>{translate(Content.map2.analysis.analysis_title)}</StyledTitle>
                    </Subtitle>
                </div>
                <InteractiveElement className="minimise" data-testid="toggle-object-details-button" onClick={() => setAnalysisToolMinimised(!isAnalysisToolsMinimised)}>
                    <span className="hidden">{translate(Content.map5.object_details.minimise)}</span>
                    <FontAwesomeIcon icon={isAnalysisToolsMinimised ? "chevrons-up" : "chevrons-down"} />
                </InteractiveElement>
            </div>
            <CSSTransition nodeRef={nodeRef} in={!isAnalysisToolsMinimised} timeout={300} className="minimise-objectDetails-transition" unmountOnExit>
                <AnalysisToolsContainer ref={nodeRef}>
                    {(selectedAnalysisTool === AnalysisType.Distance || selectedAnalysisTool === AnalysisType.WalkTime || selectedAnalysisTool === AnalysisType.FlowRate) && (
                        <div className="objectDetails-content">
                            <ExpandableButton
                                title={translate(Content.map2.analysis.distance_title)}
                                description={translate(Content.map2.analysis.distance_description)}
                                icon={["fas", "ruler-horizontal"]}
                                disableButton={geometryType === GeometryType.LineString}
                                showAdornment={geometryType === GeometryType.LineString && ExpandableAdornmentType.Chevron}
                                canShowAdditionalContent={geometryType === GeometryType.LineString}
                                className="analysis-tool"
                                dataTestId="distance-analysis-button"
                                onClick={(expanded) => handleAnalysisToolClicked(expanded, AnalysisType.Distance)}
                                initiallyExpanded={selectedAnalysisTool === AnalysisType.Distance}
                            >
                                <AnalysisDistanceComponent feature={feature as GeoJSON.Feature<GeoJSON.LineString, MapObjectProperties>} unitOfMeasurement={unitOfMeasurement} />
                            </ExpandableButton>
                            <ExpandableButton
                                title={translate(Content.map2.analysis.walk_time_title)}
                                description={translate(Content.map2.analysis.walk_time_description)}
                                icon={["fas", "person-walking"]}
                                disableButton={geometryType === GeometryType.LineString}
                                showAdornment={geometryType === GeometryType.LineString && ExpandableAdornmentType.Chevron}
                                canShowAdditionalContent={geometryType === GeometryType.LineString}
                                className="analysis-tool"
                                dataTestId="walk-time-analysis-button"
                                onClick={(expanded) => handleAnalysisToolClicked(expanded, AnalysisType.WalkTime)}
                                initiallyExpanded={selectedAnalysisTool === AnalysisType.WalkTime}
                            >
                                <AnalysisWalkTimeComponent feature={feature as GeoJSON.Feature<GeoJSON.LineString, MapObjectProperties>} unitOfMeasurement={unitOfMeasurement} />
                            </ExpandableButton>
                            <ExpandableButton
                                title={translate(Content.map2.analysis.flow_rate_title)}
                                description={translate(Content.map2.analysis.flow_rate_description)}
                                icon={["fas", "door-open"]}
                                disableButton={geometryType === GeometryType.LineString}
                                showAdornment={geometryType === GeometryType.LineString && getAdornment(AnalysisType.FlowRate)}
                                canShowAdditionalContent={geometryType === GeometryType.LineString && typeEnabled(AnalysisType.FlowRate)}
                                className="analysis-tool"
                                onClick={(expanded) => handleAnalysisToolClicked(expanded, AnalysisType.FlowRate)}
                                dataTestId="flow-rate-analysis-button"
                                initiallyExpanded={selectedAnalysisTool === AnalysisType.FlowRate}
                            >
                                <AnalysisFlowRateComponent feature={feature as GeoJSON.Feature<GeoJSON.LineString, MapObjectProperties>} unitOfMeasurement={unitOfMeasurement} />
                            </ExpandableButton>
                        </div>
                    )}
                    {(selectedAnalysisTool === AnalysisType.Area || selectedAnalysisTool === AnalysisType.Capacity || selectedAnalysisTool === AnalysisType.Count) && (
                        <div className="objectDetails-content">
                            <ExpandableButton
                                title={translate(Content.map2.analysis.area_title)}
                                description={translate(Content.map2.analysis.area_description)}
                                icon={["fas", "ruler-combined"]}
                                disableButton={geometryType === GeometryType.Polygon}
                                showAdornment={geometryType === GeometryType.Polygon && ExpandableAdornmentType.Chevron}
                                canShowAdditionalContent={geometryType !== undefined}
                                className="analysis-tool"
                                dataTestId="area-analysis-button"
                                onClick={(expanded) => handleAnalysisToolClicked(expanded, AnalysisType.Area)}
                                initiallyExpanded={selectedAnalysisTool === AnalysisType.Area}
                            >
                                <AnalysisAreaComponent feature={feature as GeoJSON.Feature<GeoJSON.Polygon, MapObjectProperties>} unitOfMeasurement={unitOfMeasurement} />
                            </ExpandableButton>
                            <ExpandableButton
                                title={translate(Content.map2.analysis.capacity_title)}
                                description={translate(Content.map2.analysis.capacity_description)}
                                icon={["fas", "users"]}
                                disableButton={geometryType === GeometryType.Polygon}
                                showAdornment={geometryType === GeometryType.Polygon && getAdornment(AnalysisType.Capacity)}
                                canShowAdditionalContent={geometryType !== undefined && typeEnabled(AnalysisType.Capacity)}
                                className="analysis-tool"
                                onClick={(expanded) => handleAnalysisToolClicked(expanded, AnalysisType.Capacity)}
                                dataTestId="capacity-analysis-button"
                                initiallyExpanded={selectedAnalysisTool === AnalysisType.Capacity}
                            >
                                <AnalysisCapacityComponent feature={feature as GeoJSON.Feature<GeoJSON.Polygon, MapObjectProperties>} unitOfMeasurement={unitOfMeasurement} />
                            </ExpandableButton>
                            <ExpandableButton
                                title={translate(Content.map2.analysis.count_title)}
                                description={translate(Content.map2.analysis.count_description)}
                                icon={["fas", "tally"]}
                                disableButton={geometryType === GeometryType.Polygon}
                                showAdornment={geometryType === GeometryType.Polygon && ExpandableAdornmentType.Chevron}
                                canShowAdditionalContent={geometryType !== undefined}
                                className="analysis-tool"
                                dataTestId="count-analysis-button"
                                onClick={(expanded) => handleAnalysisToolClicked(expanded, AnalysisType.Count)}
                                initiallyExpanded={selectedAnalysisTool === AnalysisType.Count}
                            >
                                <AnalysisCountComponent feature={feature as GeoJSON.Feature<GeoJSON.Polygon, MapObjectProperties>} mapId={mapId} layers={layers} />
                            </ExpandableButton>
                        </div>
                    )}
                    <StyledUnitSelector className="unit-selector" />
                </AnalysisToolsContainer>
            </CSSTransition>
        </PopoverContainer>
    );
};

const AnalysisToolsContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    .analysis-tool {
        padding: 20px 0px;
        ${({ theme }) => `border-bottom: 1px solid ${theme.tertiaryColors.headerBackground};`}

        &:last-of-type {
            border-bottom: none;
        }
    }
`;
const StyledTitle = styled.span`
    text-transform: uppercase;
    font-weight: 700;
`;

const StyledUnitSelector = styled(UnitSelector)`
    ${({ theme }: { theme: Theme }) =>
        `color: ${theme.typographyColors.blank}; 
         border-top: 1px solid ${theme.otherColors.separatorLight}; 
         background-color: ${theme.primaryColors.subdued50};
        `};

    padding: ${formGap} ${sidebarPadding};
    span {
        padding-right: ${formGap};
        font-size: ${fontSizes.small};
    }
    text-align: right;
    width: 100%;
    box-sizing: border-box;
`;
